import { FIELD_LABELS } from '../validation/fields.js';
import { LEAD_FIELD_LABELS } from '../leads/rules.js';
import { INTEREST_VALUES, MARKET_CODES, ROLE_VALUES } from '../vocabulary.js';

/**
 * The voice review of To do (brief sections 6.9 and 8): one question at a
 * time about what is missing, an answer understood (by Claude through the
 * API, or here when it cannot answer), confirmed ("Got it: Italy") and
 * saved at once. Shared by the screens and the API.
 */

/** The lists the review needs (config/portal.json), as the server and the app bundle them. */
type Lists = {
  countries: readonly string[];
  markets: readonly { code: string; name: string }[];
  interestOptions: readonly { value: string; label: string }[];
  roleOptions: readonly { value: string; label: string }[];
};

/** What kind of answer a field takes. */
export type VoiceKind =
  | 'text'
  | 'name'
  | 'email'
  | 'phone'
  | 'country'
  | 'markets'
  | 'market'
  | 'interest'
  | 'role'
  | 'qualification'
  | 'date';

export const VOICE_FIELDS: Record<string, VoiceKind> = {
  first_name: 'name',
  last_name: 'name',
  company: 'text',
  company_name: 'text',
  email: 'email',
  job_title: 'text',
  mobile: 'phone',
  office_phone: 'phone',
  linkedin_url: 'text',
  country: 'country',
  installation_country: 'country',
  applications: 'markets',
  deal_market: 'market',
  interest: 'interest',
  role: 'role',
  pain: 'text',
  qualification: 'qualification',
  product_fit: 'text',
  disqualified_why: 'text',
  disqualified_when: 'text',
  project_call_date: 'date',
  note: 'text',
};

/**
 * Free answers (round 2): written cleanly by Claude from what was said, then
 * always said back and confirmed before they are saved.
 */
export const VOICE_FREE_FIELDS = [
  'pain',
  'product_fit',
  'disqualified_why',
  'disqualified_when',
  'note',
] as const;

export const isFreeVoiceField = (field: string) =>
  (VOICE_FREE_FIELDS as readonly string[]).includes(field);

/**
 * Words of the trade that speech recognition often gets wrong ("Trooper"
 * heard for "throughput"): Claude is told to expect them.
 */
export const VOICE_GLOSSARY = [
  'throughput',
  'tons per hour',
  'tonnage',
  'picks per minute',
  'conveyor',
  'belt',
  'infeed',
  'MRF',
  'C&D',
  'construction and demolition',
  'bag sorting',
  'recyclables',
  'metals',
  'ferrous',
  'non-ferrous',
  'OCC',
  'PET',
  'HDPE',
  'fines',
  'ballistic separator',
  'optical sorter',
  'trommel',
  'picker',
  'sorter',
  'gate fee',
  'landfill',
  'Waste Robotics',
  'WR-1',
  'WR-2',
  'WR-3',
  'WR-4',
] as const;

/** A free answer as the device writes it when Claude cannot: a capital, a full stop. */
export function tidyFreeAnswer(text: string): string {
  const t = text.trim().replace(/\s+/g, ' ');
  if (!t) return t;
  const first = t.replace(/^\p{L}/u, (c) => c.toUpperCase());
  return /[.!?]$/.test(first) ? first : `${first}.`;
}

/** Set by the portal itself, never asked (prototype: dealStage, dealname, dealOwner). */
export const VOICE_AUTO_FIELDS = ['owner_user_id', 'stage_id', 'deal_name'] as const;

export interface VoiceRecord {
  kind: 'contact' | 'lead';
  /** "Recuperi Padana Srl", or the contact's name. */
  title: string;
  /** The (primary) contact's first name. */
  first: string;
  /** The (primary) contact's full name. */
  contactName?: string;
  company: string;
  /** The lead's company country (for "same country"). */
  country?: string;
}

/** One question of the review (prototype: ASK). */
export function voiceQuestion(field: string, r: VoiceRecord, verify?: string): string {
  const first = r.first || 'the contact';
  const company = r.company || 'the company';
  if (verify !== undefined) {
    const label = (FIELD_LABELS as Record<string, string>)[field]?.toLowerCase() ?? field;
    return `I read ${verify} as the ${label}. Is that right?`;
  }
  const q: Record<string, string> = {
    first_name: `What is the first name of your contact at ${company}?`,
    last_name: `What is ${first}'s last name?`,
    company: `What is the company name for ${r.title || first}?`,
    company_name: `What is the company name for ${r.title || 'this lead'}?`,
    email: `Do you have the email of ${first}? You can spell it.`,
    mobile: `Did you get a mobile number for ${first}?`,
    office_phone: `What is ${first}'s office phone?`,
    job_title: `What is ${first}'s job title?`,
    country: `Which country is ${company} in?`,
    installation_country: `Where would the system be installed? Same country as the company?`,
    applications: `Which operating markets: WR-1 bag sorting, WR-2 construction and demolition, WR-3 recyclables or WR-4 metals?`,
    deal_market: `Which system fits best: WR-1, WR-2, WR-3 or WR-4?`,
    interest: `Is ${company} actively looking at a project, or just browsing?`,
    role: `What is ${first}'s role: do they decide, influence, or scope it for someone else?`,
    pain: `What do they sort today, and what is the most frustrating part?`,
    qualification: `So, is it a qualified project, or not qualified for now?`,
    product_fit: `On the technical side: material, throughput, how it arrives on the belt?`,
    disqualified_why: `Why is there no product fit?`,
    disqualified_when: `And when could that change?`,
    project_call_date: `Did you book the Project Call? When?`,
  };
  return q[field] ?? `What is the ${labelOf(field).toLowerCase()}?`;
}

export const labelOf = (field: string) =>
  (LEAD_FIELD_LABELS as Record<string, string>)[field] ??
  (FIELD_LABELS as Record<string, string>)[field] ??
  field;

export type VoiceValue = string | string[];

/** How the review says a saved answer back ("Got it: Active project."). */
export function voiceSay(field: string, value: VoiceValue, lists: Lists): string {
  const kind = VOICE_FIELDS[field];
  const one = (v: string) =>
    kind === 'interest'
      ? (lists.interestOptions.find((o) => o.value === v)?.label ?? v)
      : kind === 'role'
        ? (lists.roleOptions.find((o) => o.value === v)?.label ?? v)
        : kind === 'qualification'
          ? v === 'qualified'
            ? 'Qualified'
            : 'Not qualified for now, marketing nurturing'
          : kind === 'markets' || kind === 'market'
            ? `${v} ${lists.markets.find((m) => m.code === v)?.name ?? ''}`.trim()
            : kind === 'date'
              ? new Date(`${v}T12:00:00Z`).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  timeZone: 'UTC',
                })
              : v;
  return Array.isArray(value) ? value.map(one).join(', ') : one(value);
}

const YES = /^(yes|yeah|yep|correct|right|that's right|exactly|sure|ok|okay|oui)\b/i;
const NO = /^(no|nope|wrong|not (really|right|quite)|non)\b/i;

/** A spoken yes or no; null when it is neither. */
export function yesNo(text: string): boolean | null {
  const t = text.trim();
  if (YES.test(t)) return true;
  if (NO.test(t)) return false;
  return null;
}

/** "sean at brennan recycling dot com" is sean@brennanrecycling.com. */
export function spokenEmail(text: string): string | null {
  const t = text
    .toLowerCase()
    .replace(/\s+(at|@)\s+/g, '@')
    .replace(/\s+(dot|point)\s+/g, '.')
    .replace(/\s+(dash|hyphen)\s+/g, '-')
    .replace(/\s+underscore\s+/g, '_')
    .replace(/\s+/g, '');
  return /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/.test(t) ? t : null;
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** "today", "tomorrow", "next thursday", "on the 12th" or a date: YYYY-MM-DD. */
export function spokenDate(text: string, today: string): string | null {
  const t = text.toLowerCase();
  const base = new Date(`${today}T12:00:00Z`);
  const add = (days: number) => {
    const d = new Date(base);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const iso = /(\d{4}-\d{2}-\d{2})/.exec(t);
  if (iso) return iso[1]!;
  if (/\btoday\b/.test(t)) return add(0);
  if (/\btomorrow\b/.test(t)) return add(1);
  const day = WEEKDAYS.findIndex((w) => t.includes(w));
  if (day >= 0) {
    let ahead = (day - base.getUTCDay() + 7) % 7 || 7;
    if (/\bnext week\b/.test(t)) ahead += 7;
    return add(ahead);
  }
  const nth = /\b(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)\b/.exec(t);
  if (nth) {
    const n = Number(nth[1]);
    const d = new Date(base);
    if (n < d.getUTCDate()) d.setUTCMonth(d.getUTCMonth() + 1);
    d.setUTCDate(n);
    return d.toISOString().slice(0, 10);
  }
  return null;
}

/**
 * The portal's own understanding of an answer, without Claude (no network,
 * or the AI unavailable): clear answers only; anything else is asked again.
 */
export function understandLocally(
  field: string,
  text: string,
  lists: Lists,
  context: { companyCountry?: string; today: string },
): VoiceValue | null {
  const t = text.trim();
  const s = t.toLowerCase();
  if (!t) return null;
  switch (VOICE_FIELDS[field]) {
    case 'interest':
      return /brows|curious|looking around|no project|not yet/.test(s)
        ? 'browsing'
        : /active|project|yes|buying/.test(s)
          ? 'active'
          : null;
    case 'role':
      return /no influence|not involved|none/.test(s)
        ? 'none'
        : /someone|scop|research|for (his|her|their) boss/.test(s)
          ? 'scoping'
          : /influen|recommend|operat/.test(s)
            ? 'influences'
            : /decid|sign|owner|boss/.test(s)
              ? 'decides'
              : null;
    case 'qualification':
      return /\bnot\b|no fit|nurtur|disqualif/.test(s)
        ? 'disqualified'
        : /yes|qualif|real|project/.test(s)
          ? 'qualified'
          : null;
    case 'markets': {
      const codes = MARKET_CODES.filter(
        (code, i) =>
          new RegExp(`wr[-\\s]?${i + 1}\\b`).test(s) ||
          s.includes(lists.markets[i]!.name.toLowerCase().split(' ')[0]!),
      );
      return codes.length ? codes : null;
    }
    case 'market': {
      const m = /wr[-\s]?([1-4])\b/.exec(s);
      return m ? `WR-${m[1]}` : null;
    }
    case 'country': {
      if (field === 'installation_country' && /same|yes/.test(s) && context.companyCountry) {
        return context.companyCountry;
      }
      const found = lists.countries
        .filter((c) => s.includes(c.toLowerCase()))
        .sort((a, b) => b.length - a.length)[0];
      return found ?? null;
    }
    case 'email':
      return spokenEmail(t);
    case 'phone':
      return /^[+\d][\d\s().-]{5,}$/.test(t.replace(/^plus\s*/i, '+'))
        ? t.replace(/^plus\s*/i, '+').replace(/,/g, '')
        : null;
    case 'date':
      return spokenDate(t, context.today);
    case 'name':
      return /^[\p{L}' .-]{1,60}$/u.test(t) ? t.replace(/^\p{L}/u, (c) => c.toUpperCase()) : null;
    default:
      return (isFreeVoiceField(field) ? tidyFreeAnswer(t) : t).slice(0, 2000);
  }
}

/** Checks a value (from Claude or the device) against what the field takes. */
export function voiceValueFits(field: string, value: unknown, lists: Lists): value is VoiceValue {
  switch (VOICE_FIELDS[field]) {
    case 'interest':
      return (INTEREST_VALUES as readonly unknown[]).includes(value);
    case 'role':
      return (ROLE_VALUES as readonly unknown[]).includes(value);
    case 'qualification':
      return value === 'qualified' || value === 'disqualified';
    case 'markets':
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((v) => (MARKET_CODES as readonly unknown[]).includes(v))
      );
    case 'market':
      return (MARKET_CODES as readonly unknown[]).includes(value);
    case 'country':
      return typeof value === 'string' && lists.countries.includes(value);
    case 'email':
      return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(value);
    case 'date':
      return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
    case undefined:
      return false;
    default:
      return typeof value === 'string' && value.trim() !== '' && value.length <= 2000;
  }
}
